import { TransformedXAxisType } from '@api';
import { useWindowResize } from '@helpers';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  Y_AXIS_LABEL_PERCENTAGE,
  Y_AXIS_LABEL_WIDTH,
} from '../apache-config';
import { RemoteLegend } from '../RemoteLegend';
import { tooltipFormatter } from '../utils';
import { DATA_ZOOM_OPTIONS, LENGTH_TO_SHOW_DATA_ZOOM } from './utils';

interface SingleSeries {
  name: string;
  data: (number | null)[];
  yAxisIndex?: number;
}

interface SingleYAxis {
  name: string;
  formatter?: string;
}

export interface BasicLineChartProps extends ApacheChartBase {
  /** Discriminator. Always `'apache-line'`. */
  chartType: 'apache-line';
  /** Categories rendered on the x-axis. */
  xAxis: TransformedXAxisType;
  /**
   * Optional y-axis configuration. Pass a single entry for one axis,
   * multiple entries for a multi-axis layout (left + right + offset
   * extras). Each can carry its own value formatter.
   */
  yAxis?: SingleYAxis[];
  /** Series to render. When `barData` matches a series' `name`, that series renders as bars instead of lines. */
  series: SingleSeries[];
  /** Names of series in `series` that should be rendered as bars (creates a mixed line+bar chart). */
  barData?: string[];
}

/**
 * Multi-series line chart with optional secondary y-axes and inline
 * bar series for hybrid line+bar visualizations.
 *
 * Auto-enables the `dataZoom` slider when `xAxis.length` exceeds the
 * threshold so long time-series stay legible. Renders a separate
 * `RemoteLegend` underneath, with bar/line markers per series.
 */
export const BasicLineChart = ({
  theme = 'dark_default',
  chartType,
  series,
  xAxis,
  yAxis,
  barData,
  onInit,
  isModal,
}: BasicLineChartProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const { width, height } = useWindowResize({ targetRef: chartRef });
  const [legendInstance, setLegendInstance] = useState<Types.ECharts | null>(
    null
  );

  useEffect(() => {
    if (chartRef.current) {
      const chartDom = getInstanceByDom(chartRef.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  const formattedYAxis = useMemo<
    echarts.YAXisComponentOption | echarts.YAXisComponentOption[]
  >(() => {
    if (yAxis?.length) {
      return yAxis.map((item, index) => ({
        type: 'value',
        name: item.name,
        position: index > 0 ? 'right' : 'left',
        alignTicks: true,
        offset: index > 1 ? (Y_AXIS_LABEL_WIDTH / 2) * index : 0,
        ...(item.formatter && {
          axisLabel: {
            formatter: `{value} ${item.formatter}`,
          },
        }),
      }));
    }

    if (barData) {
      const lineData = series.filter(item => !barData.includes(item.name));
      return [
        {
          type: 'value',
          name: barData[0],
        },
        {
          type: 'value',
          name: lineData?.[0]?.name || '',
          boundaryGap: ['5%', '5%'],
        },
      ];
    }

    return {
      type: 'value',
      boundaryGap: ['5%', '5%'],
    };
  }, [chartType]);

  const isShowDataZoom = xAxis.length >= LENGTH_TO_SHOW_DATA_ZOOM;
  const customGridForMultipleYAxis =
    yAxis?.length && yAxis?.length - 1 > 1
      ? {
          right: '15%',
          bottom: '5%',
        }
      : {
          bottom: '5%',
        };

  useEffect(() => {
    const chartDom = init(chartRef.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    const chartWidth = width || DEFAULT_CHART_WIDTH;
    const yAxisLabelWidth = chartWidth * Y_AXIS_LABEL_PERCENTAGE;

    const option = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        animation: false,
        yAxis: formattedYAxis,
        xAxis: {
          data: xAxis,
        },
        series: series.map(i => {
          const isBar = barData?.includes(i.name);
          const isMultipleYAxis = !!yAxis?.length;

          return {
            ...i,
            type: isBar ? 'bar' : 'line',
            ...(!isBar &&
              isMultipleYAxis && {
                yAxisIndex: i.yAxisIndex,
              }),
          };
        }),
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'none',
          },
          formatter: params => {
            return tooltipFormatter({
              isShowTitle: true,
              isShowMarker: true,
              params: params as Types.DefaultLabelFormatterCallbackParams,
            });
          },
        },
        ...(customGridForMultipleYAxis && { grid: customGridForMultipleYAxis }),
        ...(isShowDataZoom && { ...DATA_ZOOM_OPTIONS }),
        legend: {
          ...(isShowDataZoom && DATA_ZOOM_OPTIONS.legend),
          show: false,
        },
      },
    });

    chartDom.setOption(option);

    setLegendInstance(chartDom);

    if (wrapperRef.current) {
      onInit?.(wrapperRef.current);
    }

    return () => {
      chartDom.dispose();
    };
  }, [isModal, chartType, barData, series, xAxis, yAxis, width, height]);

  return (
    <div ref={wrapperRef}>
      <div ref={chartRef} />
      {legendInstance && (
        <RemoteLegend
          theme={theme}
          legendData={series.map(s => {
            const type = barData?.includes(s.name) ? 'bar' : 'line';
            return { name: s.name, type };
          })}
          targetChart={legendInstance}
        />
      )}
    </div>
  );
};
